import { useState } from "react";
import { useParams } from "react-router-dom";
import { Button, CircularProgress, Container, Paper, Stack } from "@mui/material";
import Drawer from "./Drawer";
import { Faq, NewFaq } from "@/types/faq";
import { EmptyState, SortableList } from "@/components";
import { useDeleteFaqMutation, useFetchFaqsQuery, useUpdateFaqMutation } from "@/features/organizations/api/faqApi";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import PageHeader from "@/components/PageHeader";
import { useAccessControl } from "@/features/access-control";
import AvailableQuota from "@/components/AvailableQuota";
import useOrganizationPlan from "@/hooks/useOrganizationPlan";
import { useAppSelector } from "@/hooks";

export const EMPTY_FAQ: NewFaq = {
  question: "",
  answer: "",
};

const OrganizationFaqs = () => {
  const container = useAppSelector((state) => state.ui?.container);
  const [selectedFaq, setSelectedFaq] = useState<Faq | NewFaq>();
  const { organizationId } = useParams<{ organizationId: string }>();
  const { hasAccess } = useAccessControl();
  const { checkFeatureLimit, openUpgradePopUp } = useOrganizationPlan('faq');
  const { data, isLoading } = useFetchFaqsQuery(organizationId || skipToken);
  const [deleteFaq] = useDeleteFaqMutation();
  const [updateFaq] = useUpdateFaqMutation();

  const handleDelete = (faqId: string) => {
    if (!organizationId || !faqId) return;
    deleteFaq({ organizationId, faqId });
  };

  const handleEdit = (faq: Faq) => {
    delete faq.label;
    setSelectedFaq(faq);
  };

  const handleReorder = async (faqs: Faq[]) => {
    if (!organizationId || !data) return;

    const reorderedFaqs = faqs.map((faq) => ({
      ...faq,
    }));

    await updateFaq({
      organizationId,
      faq: reorderedFaqs,
    });
  };

  const onClose = () => {
    setSelectedFaq(undefined);
  };

  const Actions = () => {
    if (hasAccess("update.organization")) {
      return (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="center" alignItems="center">
          <AvailableQuota
            feature="faq"
            usedQuota={data?.length || 0}
          />
          <Button
            size="large"
            color="success"
            type="submit"
            fullWidth={false}
            onClick={() => {
              if (checkFeatureLimit(data?.length || 0)) {
                setSelectedFaq(EMPTY_FAQ)
              } else {
                openUpgradePopUp();
              }
            }}
          >
            Crear
          </Button>
        </Stack>
      );
    }

    return null;
  }

  if (isLoading) {
    return (
      <Stack flex={1} justifyContent="center" alignItems="center">
        <CircularProgress />
      </Stack>
    );
  }

  if (!data) {
    return <EmptyState />;
  }

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Preguntas Frecuentes"
        action={<Actions />}
      />
      <Stack
        sx={{
          flex: 1,
        }}
      >
        <Container
          maxWidth={container === 'fixed' ? 'lg' : false}
          sx={{ height: '100%' }}
        >
          <Paper sx={{ padding: 2 }}>
            <Stack gap={2}>
              {data.length == 0 ? (
                <EmptyState title="Sin Preguntas Frecuentes" description="Cuando agregues preguntas frecuentes se mostrarán aquí" />
              ) : (
                <SortableList<Faq>
                  items={data.map((faq) => ({
                    ...faq,
                    label: faq.question,
                  }))}
                  onDelete={(_, deletefFaq: any) => handleDelete(deletefFaq.id)}
                  onEdit={handleEdit}
                  onChange={handleReorder}
                  deleteConfirmation
                />
              )}
            </Stack>
          </Paper>
        </Container>
      </Stack>
      <Drawer value={selectedFaq as Faq} onClose={onClose} />
    </Stack>
  );
};

export default OrganizationFaqs;
